import { useState } from 'react';
import { Spin } from 'antd';

import DownSvg from 'assets/svg/Down';
import type { AxisConfiguration, AxisPreviewResult } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import { colorConstants } from 'config/colorConstants';
import { CustomCheckbox } from './PackArchitectureTableCheckbox.styles';

type OtherGroupItemProps = {
    label: string;
    hiddenItems: string[];
};

const OtherGroupItem = ({ label, hiddenItems }: OtherGroupItemProps) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <li className="mb-1 text-sm leading-[22px] text-toolbarLabelGrey">
            <button
                type="button"
                className="flex w-full items-center justify-between gap-2 bg-transparent p-0 text-left text-sm leading-[22px] text-toolbarLabelGrey"
                onClick={() => setExpanded((current) => !current)}
                aria-expanded={expanded}
            >
                <span className="min-w-0">{label}</span>
                <span
                    className={`inline-flex shrink-0 items-center justify-center transition-transform duration-200 ${
                        expanded ? 'rotate-180' : 'rotate-0'
                    }`}
                    aria-hidden
                >
                    <DownSvg dropDownStroke={colorConstants.toolbarLabelGrey} />
                </span>
            </button>
            {expanded ? (
                <ul className="m-0 mt-1 list-none">
                    {hiddenItems.map((name) => (
                        <li key={name} className="mb-1 text-sm font-normal leading-[22px] text-toolbarLabelGrey">
                            {name}
                        </li>
                    ))}
                </ul>
            ) : null}
        </li>
    );
};

type AxisConfigurationModalPreviewProps = {
    config: AxisConfiguration;
    ownDimensionSortLabel?: string;
    previewResult: AxisPreviewResult;
    displayTotalCount: number;
    showOwnEntityFirstOption: boolean;
    isLoading?: boolean;
    onChange: (patch: Partial<AxisConfiguration>) => void;
};

const AxisConfigurationModalPreview = ({
    config,
    ownDimensionSortLabel,
    previewResult,
    displayTotalCount,
    showOwnEntityFirstOption,
    isLoading = false,
    onChange,
}: AxisConfigurationModalPreviewProps) => {
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className=" border-b border-backgroundLightGrey  ">
                <p className="m-0 mb-3 text-xs font-medium text-breadcrumbSlash">
                    Preview ({previewResult.visibleCount}/{displayTotalCount})
                </p>
                {showOwnEntityFirstOption ? (
                    <div className="flex h-[30px] items-center px-[4px] py-[4px] pl-[8px]">
                        <CustomCheckbox
                            className="axis-show-vendor-first-checkbox !mb-0"
                            checked={config.showOwnVendorFirst}
                            onChange={(event) => onChange({ showOwnVendorFirst: event.target.checked })}
                        >
                            {ownDimensionSortLabel}
                        </CustomCheckbox>
                    </div>
                ) : null}
            </div>
            <div className="relative min-h-0 max-h-[275px] flex-1 overflow-y-auto rounded-lg bg-axisModalBg px-[4px] py-[4px] pl-[8px] pr-[24px]">
                {isLoading ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
                        <Spin size="small" />
                    </div>
                ) : null}
                {!isLoading && previewResult.totalCount === 0 && displayTotalCount === 0 ? (
                    <p className="m-0 py-3 text-xs text-toolbarLabelGrey">
                        Load chart data first to see the preview.
                    </p>
                ) : (
                    <ol className="m-0 list-decimal pl-5">
                        {previewResult.entries.map((entry, index) => {
                            if (entry.kind === 'other') {
                                return (
                                    <OtherGroupItem
                                        key={`other-${index}`}
                                        label={entry.label}
                                        hiddenItems={entry.hiddenItems}
                                    />
                                );
                            }

                            return (
                                <li
                                    key={entry.name}
                                    className="mb-1 text-sm font-normal leading-[22px] text-toolbarLabelGrey"
                                >
                                    {entry.name}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
};

export default AxisConfigurationModalPreview;
